import {useSearchParams} from "react-router";

export const MONTH_COUNT = 12;

const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

export function isMonth(value: string | null | undefined): value is string {
    return value !== null && value !== undefined && MONTH_PATTERN.test(value);
}

/**
 * The current month (UTC) as YYYY-MM.
 */
export function currentMonth(): string {
    return new Date().toISOString().slice(0, 7);
}

export function shiftMonth(month: string, delta: number): string {
    const [year, monthNumber] = month.split('-').map(Number);
    return new Date(Date.UTC(year, monthNumber - 1 + delta, 1)).toISOString().slice(0, 7);
}

export function monthToDate(month: string): Date {
    return new Date(month + '-01T00:00:00Z');
}

type SearchParamChanges = Record<string, string | null | undefined>;

/**
 * The months of an analysis view, kept in the query parameters so that links to a view stay
 * shareable:
 *
 * - to: the last month of the twelve months shown, the current month if missing
 * - month: the selected month, the last completed month if missing (or the last month shown,
 *   if the range ends before it)
 *
 * Other parameters of the view, e.g. its filters, are kept when the months change.
 */
export function useAnalysisMonths() {
    const [searchParams, setSearchParams] = useSearchParams();
    const now = currentMonth();

    const toParameter = searchParams.get('to');
    const to = isMonth(toParameter) && toParameter <= now ? toParameter : now;
    const from = shiftMonth(to, -(MONTH_COUNT - 1));
    const isInRange = (month: string) => month >= from && month <= to;

    const lastCompletedMonth = shiftMonth(now, -1);
    const defaultMonth = isInRange(lastCompletedMonth) ? lastCompletedMonth : to;
    const monthParameter = searchParams.get('month');
    const selectedMonth = isMonth(monthParameter) && isInRange(monthParameter) ? monthParameter : defaultMonth;

    const update = (changes: SearchParamChanges) => {
        setSearchParams(previous => {
            const next = new URLSearchParams(previous);
            Object.entries(changes).forEach(([key, value]) => {
                if (value === null || value === undefined || value === '') {
                    next.delete(key);
                } else {
                    next.set(key, value);
                }
            });
            return next;
        }, {replace: true});
    };

    const selectMonth = (month: string) => update({month: month === defaultMonth ? null : month});

    const shiftRange = (delta: number) => {
        const newTo = shiftMonth(to, delta) > now ? now : shiftMonth(to, delta);
        const newFrom = shiftMonth(newTo, -(MONTH_COUNT - 1));
        update({
            to: newTo === now ? null : newTo,
            month: selectedMonth >= newFrom && selectedMonth <= newTo ? searchParams.get('month') : null,
        });
    };

    return {
        searchParams,
        range: {from, to},
        selectedMonth,
        selectMonth,
        shiftRange,
        canShiftForward: to < now,
        update,
    };
}
